import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from "next/image";
import { useTranslation } from 'next-i18next';
import { FaFacebookF, FaInstagram, FaYoutube, FaWhatsapp, FaVimeo } from 'react-icons/fa';
import { MdPhone, MdEmail, MdLocationOn, MdAccessTime } from 'react-icons/md';
import { getCurrentLocationAndLanguage, getCountryInfo, LocationKey } from '@utils/location';
import { IconType } from 'react-icons';
import TwitterIcon from '@components/Svg/twitter-icon-x';
import { FaLock, FaUserShield, FaDollarSign, FaGlobe, FaLiraSign } from 'react-icons/fa';
import SkeletonFooter from '@components/ui-old/loaders/footer-loader';
import { getThemedImage } from '@components/layout/get-themed-image';
import Cookies from 'js-cookie';
import DataProjectDialog from '@components/other/data-project-dialog';
import { TbTruckDelivery } from 'react-icons/tb';
const Footer: React.FC = () => {
  const {
    t
  } = useTranslation('footer');
  const [location, setLocation] = useState<string | null>(null);
  const [language, setLanguage] = useState<string | null>(null);
  const [selectedLocationIndex, setSelectedLocationIndex] = useState(0);
  useEffect(() => {
    const {
      location: detectedLocation
    } = getCurrentLocationAndLanguage();
    setLocation(detectedLocation);
    const activeLang = Cookies.get('NEXT_LOCALE') || 'en';
    setLanguage(activeLang);
  }, []);
  if (!location) return <SkeletonFooter />;
  const countryInfo = getCountryInfo(location);
  if (!countryInfo) return null;

  // Define the structure for each schedule entry
  type ScheduleEntry = {
    days: string;
    hours: string;
  };

  // Define the structure for schedule information per country
  type ScheduleInfo = { [key in LocationKey]?: ScheduleEntry[] };

  // Define the structure for contact information per country
  type ContactInfo = { [key in LocationKey]?: {
    locations: {
      name: string;
      address: string;
      phone: string;
      whatsapp?: string;
      email: string;
      mapsUrl: string;
    }[];
  } };
  interface AdditionalLink {
    id: string;
    text: string;
    url: string;
    newTab?: boolean;
    icon: React.ReactNode;
  }

  // Define the structure for social media links
  type SocialLink = {
    Icon: IconType;
    url: string;
  };

  // Updated scheduleInfo: Supports multiple working hours for some countries
  const scheduleInfo: ScheduleInfo = {
    tr: [{
      days: t('monday-friday'),
      hours: '09:00 - 19:00'
    }],
    az: [{
      days: t('monday-friday'),
      hours: '10:00 - 18:00'
    }],
    kg: [{
      days: t('monday-friday'),
      hours: `10:00 - 18:00 (${t('sales-time-mon-kg')})`
    }, {
      days: t('day-saturday'),
      hours: `12:00 - 17:00 (${t('sales-time-sat-kg')})`
    }, {
      days: t('day-sunday'),
      hours: `12:00 - 17:00 (${t('sales-time-sun-kg')})`
    }],
    ge: [{
      days: t('monday-friday'),
      hours: '11:00 - 18:00'
    }],
    iq: [{
      days: t('Sunday-Thursday'),
      hours: '09:00 - 18:00'
    }],
    kz: [{
      days: t('monday-friday'),
      hours: '10:00 - 17:30'
    }, {
      days: t('wednesday-friday'),
      hours: '11:00 - 15:30'
    }, {
      days: t('day-saturday'),
      hours: '11:00 - 15:30'
    }],
    uz: [{
      days: t('monday-friday'),
      hours: '10:00 - 18:00'
    }, {
      days: t('day-saturday'),
      hours: '10:00 - 15:00'
    }]
  };

  // Social media links categorized by country
  const socialLinksByCountry: { [key in LocationKey]?: SocialLink[] } = {
    tr: [{
      Icon: FaFacebookF,
      url: 'https://www.facebook.com/foreverturkeyhq/'
    }, {
      Icon: TwitterIcon,
      url: 'https://twitter.com/ForeverTurkeyhq'
    }, {
      Icon: FaInstagram,
      url: 'https://www.instagram.com/foreverturkeyhq'
    }, {
      Icon: FaYoutube,
      url: 'https://www.youtube.com/user/AloeTurkey/videos'
    }],
    az: [{
      Icon: FaFacebookF,
      url: 'https://www.facebook.com/foreverturkeyhq/'
    }, {
      Icon: TwitterIcon,
      url: 'https://x.com/ForeverGlobalHQ'
    }, {
      Icon: FaInstagram,
      url: 'https://www.instagram.com/foreverazerbaijanhq'
    }, {
      Icon: FaVimeo,
      url: 'https://vimeo.com/foreverglobalhq'
    }],
    kg: [{
      Icon: FaFacebookF,
      url: 'https://www.facebook.com/flpkyrgyzstanhq/'
    }, {
      Icon: TwitterIcon,
      url: 'https://x.com/ForeverGlobalHQ'
    }, {
      Icon: FaInstagram,
      url: 'https://www.instagram.com/foreverkyrgyzstanhq/'
    }, {
      Icon: FaYoutube,
      url: 'https://www.youtube.com/channel/UCmZngtTsj-T2K7YABhbGYLQ'
    }],
    ge: [{
      Icon: FaFacebookF,
      url: 'https://www.facebook.com/forevergeorgiahq/'
    }, {
      Icon: TwitterIcon,
      url: 'https://x.com/ForeverGlobalHQ'
    }, {
      Icon: FaInstagram,
      url: 'https://www.instagram.com/forevergeorgiahq/'
    }, {
      Icon: FaVimeo,
      url: 'https://vimeo.com/foreverglobalhq'
    }],
    iq: [{
      Icon: FaFacebookF,
      url: 'https://www.facebook.com/Foreveriraqhq/'
    }, {
      Icon: TwitterIcon,
      url: 'https://x.com/ForeverGlobalHQ'
    }, {
      Icon: FaInstagram,
      url: 'https://www.instagram.com/foreveriraqhq/'
    }, {
      Icon: FaVimeo,
      url: 'https://vimeo.com/foreverglobalhq'
    }],
    kz: [{
      Icon: FaFacebookF,
      url: 'https://www.facebook.com/foreverazerbaijan/'
    }, {
      Icon: TwitterIcon,
      url: 'https://x.com/ForeverGlobalHQ'
    }, {
      Icon: FaInstagram,
      url: 'https://www.instagram.com/foreverazerbaijan/'
    }, {
      Icon: FaVimeo,
      url: 'https://vimeo.com/foreverglobalhq'
    }],
    uz: [{
      Icon: FaFacebookF,
      url: 'https://www.facebook.com/foreveruzbekistanhq/'
    }, {
      Icon: TwitterIcon,
      url: 'https://x.com/ForeverGlobalHQ'
    }, {
      Icon: FaInstagram,
      url: 'https://www.instagram.com/foreveruzbekistanhq/'
    }, {
      Icon: FaYoutube,
      url: 'https://www.youtube.com/@FLPUzbekistan'
    }]
  };

  // Retrieve the social links based on the current location
  const socialLinks: SocialLink[] = socialLinksByCountry[location as LocationKey] || [];

  // Contact information categorized by country
  const contactInfo: ContactInfo = {
    tr: {
      locations: [{
        name: 'İSTANBUL',
        address: 'Gazeteciler Sitesi Yazarlar Sokak No:8 Esentepe Mahallesi Şişli/İstanbul',
        phone: '+90 (212) 347 71 26',
        whatsapp: '+90 539 661 72 77',
        email: 'info@flptr.com',
        mapsUrl: 'https://maps.app.goo.gl/NZ58L8mdbu2VX9Wv6'
      }, {
        name: 'İZMİR',
        address: 'İsmet Kaptan Mahallesi 1371 Sokak No.3/A, Çankaya - Konak / Izmir',
        phone: '+90 (232) 445 63 44',
        whatsapp: '+90 539 661 72 77',
        email: 'info@flptr.com',
        mapsUrl: 'https://maps.app.goo.gl/YDEPrrcpaRMQEoiB8'
      }]
    },
    az: {
      locations: [{
        name: 'BAKU',
        address: t('text-baku-address'),
        phone: '+994 12 465 43 96',
        // whatsapp: '+994 12 465 43 96',
        email: 'flpazerbaijan@yahoo.com',
        mapsUrl: 'https://maps.app.goo.gl/XcFMZFTHAHBcEmhy6'
      }]
    },
    kz: {
      locations: [{
        name: 'Almaty',
        address: 'Almaty m-district Zhetysu - 2House 15 А',
        phone: '+7 (727) 374 97 37',
        //  whatsapp: '+7 (727) 374 97 37',
        email: 'flpkazakhstan@gmail.com',
        mapsUrl: 'https://goo.gl/maps/mVEaU12H74qoKFSAA'
      }, {
        name: 'Astana',
        address: 'Astana Sh. Ualihanov str. House 12',
        phone: '+7 (7172) 79 50 35',
        // whatsapp: '+7 (7172) 79 50 35',
        email: 'flpastana@flpkz.kz',
        mapsUrl: 'https://go.2gis.com/e27e1'
      }, {
        name: 'Atyrau',
        address: 'Atyrau, Avangard 2-nd m-district, House 11-B, Block 4, ground floor, place #3 ("Modern Stroi")',
        phone: '+7 (7122) 507-000',
        //  whatsapp: '+7 (7122) 507-000',
        email: 'flpatyrau@flpkz.kz',
        mapsUrl: 'https://goo.gl/maps/Uixjb6cRvUT2hbiY9'
      }]
    },
    kg: {
      locations: [{
        name: 'Bishkek',
        address: t('text-bishkek-address'),
        phone: '0 (312) 62 49 06',
        whatsapp: '0 (555) 62 49 06',
        email: 'flpkyrgyzstan@yahoo.com',
        mapsUrl: 'https://goo.gl/maps/bpwfdSksBDuD7CHk7'
      }
      // {
      //   name: 'Osh',
      //   address: 'Osh Shakirova str. 275, Kyrgyzstan',
      //   phone: '0 (555) 62 49 06',
      //   whatsapp: '0 (555) 62 49 06',
      //   email: 'flpkyrgyzstan@yahoo.com',
      //   mapsUrl: 'https://goo.gl/maps/gaSqrKtMERxvvDB77',
      // },
      ]
    },
    ge: {
      locations: [{
        name: 'Tbilisi',
        address: t('text-tblisi-address'),
        phone: '+995 59 905 99 39',
        whatsapp: '+995 59 905 99 39',
        email: 'flpgeorgia@yahoo.com',
        mapsUrl: 'https://maps.app.goo.gl/FNXvvg2DvyDBgscS6'
      }]
    },
    iq: {
      locations: [{
        name: 'Erbil',
        address: 'Zaniari District No:30/89 Behind the First Erbil Tower (Francois Hariri Stadium) Erbil, Iraq',
        phone: '+964 7508208001',
        // whatsapp: '+964 7508208001',
        email: 'flpiraq@ymail.com',
        mapsUrl: 'https://maps.app.goo.gl/AjNK34PsFNgJMZTf8'
      }]
    },
    uz: {
      locations: [{
        name: 'Tashkent',
        address: t('text-tashkent-address'),
        phone: '+998 91 774 79 77',
        // whatsapp: '+555 55 555 55 55',
        email: 'flpuzbekistan@gmail.com',
        mapsUrl: 'https://maps.app.goo.gl/CUnjpWygBQv373AS7'
      }]
    }
  };

  // Legal text categorized by location
  const legalTextByLocation: { [key in LocationKey]: string } = {
    tr: "© 2025 Forever Living Sağlık ve Güzellik Ürünleri Dağıtım Ltd. Şti.",
    az: "© 2025 Forever Living Products Azərbaycan MMC",
    kz: "© 2025 Forever Living Қазақстан Денсаулық және Сұлулық Өнімдері",
    kg: "© 2025 Форевер Ливинг Продактс Кыргызстан",
    ge: "© 2025 Forever Living Products Georgia",
    iq: "© 2025 Forever Living Products Iraq",
    uz: "© 2025 Forever Product Bek LLC "
  };

  // Static country flags with corresponding links
  const countryFlags = [{
    code: 'aze',
    link: 'https://flpaze.com'
  }, {
    code: 'geo',
    link: 'https://flp.ge'
  }, {
    code: 'irq',
    link: 'https://flpirq.com'
  }, {
    code: 'kaz',
    link: 'https://flpkaz.com'
  }, {
    code: 'kgz',
    link: 'https://flpkgz.com'
  }, {
    code: 'tur',
    link: 'https://flptr.com'
  }, {
    code: 'uz',
    link: 'https://flpuz.com'
  }];

  // Static additional links
  const additionalLinksByLocation: { [key in LocationKey]?: AdditionalLink[] } = {
    tr: [{
      id: 'dataProtect',
      text: t('data-protect'),
      url: '#',
      newTab: true,
      icon: <FaLock />
    }, {
      id: 'privacy',
      text: t('privacy'),
      url: '/privacy/tr/tr/',
      newTab: true,
      icon: <FaUserShield />
    }, {
      id: 'incomeDisclosure',
      text: t('income-disclosure'),
      url: 'https://foreverliving.com/income-disclosure',
      newTab: true,
      icon: <FaLiraSign />
    }, {
      id: 'globalSite',
      text: t('global-site'),
      url: 'https://www.foreverliving.com',
      newTab: true,
      icon: <FaGlobe />
    }],
    uz: [{
      id: 'privacy',
      text: t('privacy'),
      url: 'https://online.fliphtml5.com/yrbyn/orcf/#p=1',
      newTab: true,
      icon: <FaUserShield />
    }, {
      id: 'incomeDisclosure',
      text: t('income-disclosure'),
      url: 'https://foreverliving.com/income-disclosure',
      newTab: true,
      icon: <FaDollarSign />
    }, {
      id: 'globalSite',
      text: t('global-site'),
      url: 'https://www.foreverliving.com',
      newTab: true,
      icon: <FaGlobe />
    }],
    ge: [{
      id: 'privacy',
      text: t('privacy'),
      url: 'https://online.fliphtml5.com/yrbyn/orcf/#p=1',
      newTab: true,
      icon: <FaUserShield />
    }, {
      id: 'companyPolicy',
      text: t('company-policy'),
      url: 'https://flp.ge/downloads/ge/Company_Policy_Geo.pdf',
      newTab: true,
      icon: <FaLock />
    }, {
      id: 'globalSite',
      text: t('global-site'),
      url: 'https://www.foreverliving.com',
      newTab: true,
      icon: <FaGlobe />
    }],
    iq: [{
      id: 'privacy',
      text: t('privacy'),
      url: 'https://online.fliphtml5.com/yrbyn/orcf/#p=1',
      newTab: true,
      icon: <FaUserShield />
    }, {
      id: 'globalSite',
      text: t('global-site'),
      url: 'https://www.foreverliving.com',
      newTab: true,
      icon: <FaGlobe />
    }],
    az: [{
      id: 'privacy',
      text: t('privacy'),
      url: 'https://online.fliphtml5.com/yrbyn/orcf/#p=1',
      newTab: true,
      icon: <FaUserShield />
    }, {
      id: 'salesAggrement',
      text: t('sales-agreement'),
      url: '/sales-agreement',
      newTab: true,
      icon: <FaDollarSign />
    }, {
      id: 'globalSite',
      text: t('global-site'),
      url: 'https://www.foreverliving.com',
      newTab: true,
      icon: <FaGlobe />
    }],
    kz: [{
      id: 'privacy',
      text: t('privacy'),
      url: 'https://online.fliphtml5.com/yrbyn/orcf/#p=1',
      newTab: true,
      icon: <FaUserShield />
    }, {
      id: 'incomeDisclosure',
      text: t('income-disclosure'),
      url: 'https://foreverliving.com/income-disclosure',
      newTab: true,
      icon: <FaDollarSign />
    }, {
      id: 'globalSite',
      text: t('global-site'),
      url: 'https://www.foreverliving.com',
      newTab: true,
      icon: <FaGlobe />
    }],
    kg: [{
      id: 'privacy',
      text: t('privacy'),
      url: 'https://online.fliphtml5.com/yrbyn/orcf/#p=1',
      newTab: true,
      icon: <FaUserShield />
    }, {
      id: 'salesAggrement',
      text: t('sales-agreement'),
      url: 'https://www.flpkgz.com/downloads/kg/privacy/public_oferta.pdf',
      newTab: true,
      icon: <FaDollarSign />
    }, {
      id: 'paymentdelivery',
      text: t('payment-delivery'),
      url: 'https://www.flpkgz.com/downloads/kg/docs/delivery-pay-ru.pdf',
      newTab: true,
      icon: <TbTruckDelivery />
    }, {
      id: 'globalSite',
      text: t('global-site'),
      url: 'https://www.foreverliving.com',
      newTab: true,
      icon: <FaGlobe />
    }]
  };
  const linkMapping: {
    [key: string]: {
      [linkId: string]: string;
    };
  } = {
    'ge-ka': {
      privacy: 'https://flp.ge/downloads/ge/privacy/PrivacyNotice-FLPGE.pdf'
    },
    'uz-uz': {
      privacy: 'https://flpuz.com/downloads/uz/privacy/Privacy_Notice-FLPUZ-uz.pdf'
    },
    'uz-ru': {
      privacy: 'https://flpuz.com/downloads/uz/privacy/Privacy_Notice-FLPUZ-ru.pdf'
    },
    'az-az': {
      privacy: 'https://flpaze.com/downloads/az/privacy/PrivacyNotice-FLPAZ.pdf'
    },
    'kg-ru': {
      privacy: 'https://www.flpkgz.com/downloads/kg/privacy/Privacy_Notice-FLPKG-ru.pdf'
    },
    'kg-ky': {
      privacy: 'https://www.flpkgz.com/downloads/kg/privacy/Privacy_Notice-FLPKG-kyrg.pdf'
    },
    'tr-en': {
      privacy: 'https://online.fliphtml5.com/yrbyn/orcf/#p=1'
    }
  };
  const defaultAdditionalLinks: AdditionalLink[] = [{
    id: 'privacy',
    text: t('privacy'),
    url: 'https://online.fliphtml5.com/yrbyn/orcf/#p=1',
    newTab: true,
    icon: <FaUserShield />
  }, {
    id: 'incomeDisclosure',
    text: t('income-disclosure'),
    url: 'https://foreverliving.com/income-disclosure',
    newTab: true,
    icon: <FaDollarSign />
  }, {
    id: 'globalSite',
    text: t('global-site'),
    url: 'https://www.foreverliving.com',
    newTab: true,
    icon: <FaGlobe />
  }];
  let additionalLinksForLocation = additionalLinksByLocation[location as LocationKey] || defaultAdditionalLinks;
  const mappingKey = `${location}-${language}`;
  const currentMapping = linkMapping[mappingKey];
  if (currentMapping) {
    additionalLinksForLocation = additionalLinksForLocation.map(link => {
      if (link.id && currentMapping[link.id]) {
        return {
          ...link,
          url: currentMapping[link.id],
          newTab: true
        };
      }
      return link;
    });
  }
  const additionalLinks = additionalLinksForLocation;

  // Store images categorized by country
  // const storeImagesByCountry: { [key in LocationKey]?: { googlePlay: string; appStore: string } } = {
  //   tr: {
  //     googlePlay: '/assets/images/app/google-play-tr.png',
  //     appStore: '/assets/images/app/ios-black-tr.png',
  //   },
  //   az: {
  //     googlePlay: '/assets/images/app/google-play-az.png',
  //     appStore: '/assets/images/app/ios-black-az.png',
  //   },
  //   kg: {
  //     googlePlay: '/assets/images/app/google-play-ru.png',
  //     appStore: '/assets/images/app/google-play-ru.png',
  //   },
  //   ge: {
  //     googlePlay: '/assets/images/app/google-play-en.png',
  //     appStore: '/assets/images/app/google-play-en.png',
  //   },
  //   iq: {
  //     googlePlay: '/assets/images/app/google-play-en.png',
  //     appStore: '/assets/images/app/google-play-en.png',
  //   },
  //   kz: {
  //     googlePlay: '/assets/images/app/google-play-ru.png',
  //     appStore: '/assets/images/app/google-play-ru.png',
  //   },
  //   uz: {
  //     googlePlay: '/assets/images/app/google-play-ru.png',
  //     appStore: '/assets/images/app/google-play-ru.png',
  //   },
  // };

  const paymentIconsByCountry: { [key in LocationKey]?: string } = {
    tr: '/assets/images/ssl/sectigo-logo.svg',
    az: '/assets/images/ssl/sectigo-logo.svg',
    kg: '/assets/images/ssl/sectigo-logo.svg',
    ge: '/assets/images/ssl/sectigo-logo.svg',
    iq: '/assets/images/letsencrypt-logo.svg',
    kz: '/assets/images/ssl/sectigo-logo.svg',
    uz: '/assets/images/ssl/sectigo-logo.svg'
  };
  const paymentIcon = paymentIconsByCountry[location as LocationKey] || '/assets/images/ssl/sectigo-logo.svg';

  // Retrieve store images based on the current location
  //const storeImages = storeImagesByCountry[location as LocationKey];

  // Get contact details for the selected location
  const contactDetails = contactInfo[location as LocationKey]?.locations[selectedLocationIndex];
  return <footer className="bg-gray-100 dark:bg-zinc-900 pt-10 pb-6 mt-10 rounded-t-3xl" data-sentry-component="Footer" data-sentry-source-file="footer.tsx">
      <div className="container mx-auto px-4">
        <div className="flex flex-wrap justify-between text-center md:text-left">
          {/* Logo and Social Media Section */}
          <div className="w-full md:w-1/4 mb-8">
            <Link href="/" data-sentry-element="Link" data-sentry-source-file="footer.tsx">

              <Image src="/assets/images/foreverlogogold.9d119137.png" alt="Forever Living Logo" width={280} height={60} className="mb-6 mx-auto md:mx-0" style={{
              maxWidth: "100%",
              height: "auto"
            }} data-sentry-element="Image" data-sentry-source-file="footer.tsx" />

            </Link>
            <div className="flex justify-center md:justify-start space-x-2 mt-8">
              {countryFlags.map(({
              code,
              link
            }) => <a key={code} href={link} target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform duration-200">
                  <Image src={`/assets/images/flags/${code}.png`} alt={code} width={35} height={25} className="object-contain" style={{
                maxWidth: "100%",
                height: "auto"
              }} />
                </a>)}
            </div>
            {/* <div className="flex justify-center md:justify-start space-x-4 mt-6 mb-4">
              {socialLinks.map(({ Icon, url }: SocialLink, index: number) => (
                <a key={index} href={url} target="_blank" rel="noopener noreferrer" className="text-gray-600 dark:text-neutral-300 hover:text-gray-800">
                  <Icon size={20} />
                </a>
              ))}
             </div> */}
            {/* Working Hours Card */}
            <div className="border border-gray-300 dark:border-neutral-700 shadow-md rounded-lg p-4 mt-6 mx-auto w-full md:w-auto text-center md:text-left">
              <h4 className="text-sm font-semibold mb-3">{t('common:working-hours')}</h4>
              {scheduleInfo[location as LocationKey]?.map((schedule, index) => <p key={index} className="flex items-center justify-center md:justify-start mb-2 text-sm">
                  <MdAccessTime className="mr-2 text-gray-600 dark:text-neutral-300" />
                  <span className="text-gray-700 dark:text-neutral-400">{schedule.days}: {schedule.hours}</span>
                </p>)}
            </div>
          </div>

          {/* Contact Us Section */}
          <div className="w-full md:w-1/3 mb-8">
            <h4 className="text-xl font-bold mb-4">{t('link-contact-us')}</h4>
            <div className="flex justify-center md:justify-start space-x-4 mb-2">
              {contactInfo[location as LocationKey]?.locations.map((loc, index) => <button key={index} className={`font-semibold ${selectedLocationIndex === index ? 'underline text-black dark:text-neutral-200' : 'text-neutral-500'}`} onClick={() => setSelectedLocationIndex(index)}>
                  {loc.name}
                </button>)}
            </div>
            {contactDetails && <>
                {/* Address */}
                <p className="flex justify-center md:justify-start items-start mb-2">
                  <MdLocationOn className="mr-1 mt-1 flex-shrink-0 text-gray-600 dark:text-neutral-300" />
                  <a href={contactDetails.mapsUrl} target="_blank" rel="noopener noreferrer" className="hover:underline text-gray-700 dark:text-neutral-400">
                    {contactDetails.address}
                  </a>
                </p>
                {/* Phone */}
                <p className="flex justify-center md:justify-start items-center mb-2">
                  <MdPhone className="mr-2 text-gray-600 dark:text-neutral-300" />
                  <a href={`tel:${contactDetails.phone}`} className="hover:underline text-gray-700 dark:text-neutral-400">
                    {contactDetails.phone}
                  </a>
                </p>
                {/* WhatsApp */}
                {location !== 'uz' && contactDetails.whatsapp && <p className="flex justify-center md:justify-start items-center mb-2">
                    <FaWhatsapp className="mr-2 text-gray-600 dark:text-neutral-300" />
                    <a href={`https://wa.me/${contactDetails.whatsapp.replace(/[^\d]/g, '')}`} target="_blank" rel="noopener noreferrer" className="hover:underline text-gray-700 dark:text-neutral-400">
                      {contactDetails.whatsapp}
                    </a>
                  </p>}
                {/* Email */}
                <p className="flex justify-center md:justify-start items-center mb-2">
                  <MdEmail className="mr-2 text-gray-600 dark:text-neutral-300" />
                  <a href={`mailto:${contactDetails.email}`} className="hover:underline text-gray-700 dark:text-neutral-400">
                    {contactDetails.email}
                  </a>
                </p>
              </>}
                        <div className="flex justify-center md:justify-start space-x-4 mt-6 mb-4">
              {socialLinks.map(({
              Icon,
              url
            }: SocialLink, index: number) => <a key={index} href={url} target="_blank" rel="noopener noreferrer" className="text-gray-600 dark:text-neutral-300 hover:text-gray-800">
                  <Icon size={20} />
                </a>)}
            </div>
          </div>

          {/* Fast Links and App Store Section */}
          <div className="w-full md:w-1/4">
            <h4 className="text-xl font-bold mb-2">{t('fast-link')}</h4>
            <ul className="space-y-2 mb-6 text-center md:text-left">
              {additionalLinks.map((link, index) => {
              if (link.id === 'dataProtect') {
                return <li key={index}>
                      <DataProjectDialog>
                        <button className="flex items-center justify-center md:justify-start text-center w-full hover:underline text-gray-700 dark:text-neutral-400">
                          {link.icon && <span className="mr-2">{link.icon}</span>}
                          {link.text}
                        </button>
                      </DataProjectDialog>
                    </li>;
              }
              return <li key={index}>
                    <Link href={link.url} target={link.newTab ? "_blank" : "_self"} rel={link.newTab ? "noopener noreferrer" : undefined} className="flex items-center justify-center md:justify-start text-center w-full hover:underline text-gray-700 dark:text-neutral-400">
                      {link.icon && <span className="mr-2">{link.icon}</span>}
                      {link.text}
                    </Link>
                  </li>;
            })}
            </ul>
            <div className="flex justify-center md:justify-start items-center mb-4">
              <>
                <Image src="/assets/images/dsa.eb3435cb.png" alt="DSA Logo" width={25} height={25} className="mr-2 block dark:hidden" style={{
                maxWidth: "100%",
                height: "auto"
              }} data-sentry-element="Image" data-sentry-source-file="footer.tsx" />
                <Image src="/assets/images/dsa_white.png" alt="DSA Logo" width={25} height={25} className="mr-2 hidden dark:block" style={{
                maxWidth: "100%",
                height: "auto"
              }} data-sentry-element="Image" data-sentry-source-file="footer.tsx" />
              </>
              <a href="https://www.dsa.org/" target="_blank" rel="noopener noreferrer" className='px-2 text-gray-700 dark:text-neutral-400'>
                {t('dsa-member')}
              </a>
            </div>
            {/* <div className="flex justify-center md:justify-start space-x-4">
              {storeImages && (
                <>
                  <a
                    href="http://onelink.to/dcctd8"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:opacity-75 transition-opacity"
                  >
                    <Image
                      src={storeImages.googlePlay}
                      alt="Google Play"
                      width={160}
                      height={48}
                      style={{
                        maxWidth: "100%",
                        height: "auto"
                      }} />
                  </a>
                  <a
                    href="http://onelink.to/dcctd8"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:opacity-75 transition-opacity"
                  >
                    <Image
                      src={storeImages.appStore}
                      alt="App Store"
                      width={160}
                      height={48}
                      style={{
                        maxWidth: "100%",
                        height: "auto"
                      }} />
                  </a>
                </>
              )}
             </div> */}
          </div>
        </div>
      </div>
      {/* Footer Bottom Section */}
      <div className="mt-8 pt-4 pb-4 border-t border-gray-300 dark:border-neutral-700">
        <div className="container mx-auto px-4 flex flex-col md:flex-row justify-between items-center text-center md:text-left mb-6 md:mb-0">
          {/* Legal Text */}
          <p className="text-sm text-neutral-900 dark:text-neutral-300 mb-10 md:mb-0">
            {legalTextByLocation[location as LocationKey] || "© 2025 Forever Living"}
          </p>
          {/* Payment Icons */}
          <div className="flex justify-center md:justify-start space-x-4 md:mt-0 mt-2">
            {location === 'uz' && <>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 32 32"><g id="System" fill-rule="evenodd" clip-rule="evenodd"><g id="Uzcard" fill="#373D77"><path d="M25.333 16.724c0 3.36-.774 5.864-2.32 7.496q-2.324 2.447-7.16 2.447-4.515 0-6.85-2.447-2.337-2.448-2.336-7.381V5.803c0-.259.212-.468.472-.47h5.458c.261.002.472.211.473.47V17.08c0 1.343.25 2.377.76 3.106a2.774 2.774 0 0 0 4.355.065q.747-1.027.744-3.025v-2.112h6.404z"></path><path d="M18.93 13.88V5.824c.001-.27.222-.49.495-.49h.884c.002 2.75 2.25 4.979 5.023 4.979l.001 3.567z"></path></g><path id="Uzcard_2" fill="#FF8201" d="M21.333 5.333h3.475c.29.001.524.236.525.526v3.474c-2.209 0-4-1.789-4-3.995z"></path></g></svg>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 32 32"><g id="System"><g id="Humo"><path fill="#C9BC87" d="M20.312 6.83c-.065.082-.206.224-.164.346s.297.093.404.148c.184.095.258.285.253.487-.007.235-.01 2.488-.018 5.593q-3.99-.023-7.978-.048a17 17 0 0 1 .482-5.521c.09-.356.34-.793.313-1.166a.25.25 0 0 0-.045-.14c-.072-.09-.209-.076-.319-.047a4.47 4.47 0 0 0-1.854 1.03 5 5 0 0 0-.973 1.277c-1.024 1.884-1.104 4.552-1.104 4.552-.052 1.726-.118 5.222 1.67 8.851 0 0 1.254 2.545 3.046 3.172.354.124.45.042.48.001.14-.184-.16-.5-.248-.64a6 6 0 0 1-.423-.819 9.2 9.2 0 0 1-.536-1.83c-.375-1.903-.617-3.829-.565-5.772h8.054v7.304c0 .484.192.949.627 1.176.261.137.557.186.85.223.51.065 1.03.101 1.535.002.296-.059.59-.169.811-.379.355-.339.36-.798.36-1.26 0-3.668.034-7.336.03-11.005q-.003-1.365-.018-2.73c-.015-1.234-.388-2.479-1.45-3.172A2.77 2.77 0 0 0 22.035 6c-.645 0-1.27.258-1.723.83"></path><path fill="url(#paint0_angular_2835_30722)" d="M20.312 6.83c-.065.082-.206.224-.164.346s.297.093.404.148c.184.095.258.285.253.487-.007.235-.01 2.488-.018 5.593q-3.99-.023-7.978-.048a17 17 0 0 1 .482-5.521c.09-.356.34-.793.313-1.166a.25.25 0 0 0-.045-.14c-.072-.09-.209-.076-.319-.047a4.47 4.47 0 0 0-1.854 1.03 5 5 0 0 0-.973 1.277c-1.024 1.884-1.104 4.552-1.104 4.552-.052 1.726-.118 5.222 1.67 8.851 0 0 1.254 2.545 3.046 3.172.354.124.45.042.48.001.14-.184-.16-.5-.248-.64a6 6 0 0 1-.423-.819 9.2 9.2 0 0 1-.536-1.83c-.375-1.903-.617-3.829-.565-5.772h8.054v7.304c0 .484.192.949.627 1.176.261.137.557.186.85.223.51.065 1.03.101 1.535.002.296-.059.59-.169.811-.379.355-.339.36-.798.36-1.26 0-3.668.034-7.336.03-11.005q-.003-1.365-.018-2.73c-.015-1.234-.388-2.479-1.45-3.172A2.77 2.77 0 0 0 22.035 6c-.645 0-1.27.258-1.723.83"></path><path fill="#C9BC87" d="M7.857 11.274c-1.063 1.3-1.146 2.817-1.198 4.325-.12 3.541 1.142 6.06 1.425 6.602a11.5 11.5 0 0 0 1.53 2.248 10.7 10.7 0 0 0 2.013 1.771 6 6 0 0 1-1.742-.746 6.1 6.1 0 0 1-1.356-1.164c-.421-.551-.717-1.036-.911-1.38-.519-.921-1.165-2.068-1.495-3.605-.093-.432-.155-.87-.32-.888-.24-.025-.511.849-.57 1.037A5.1 5.1 0 0 0 5 20.944c.014 1.16.863 3.417 2.895 4.948 0 0 1.776 1.338 4.813 1.44.124.005.86-.013 1.406-.413.268-.196.445-.497-.031-.64-3.263-.977-4.536-4.514-5.233-7.583-.152-.668-.296-1.327-.383-2.01-.26-2.063-.25-4.332.405-6.322-.498.114-.724.553-1.015.91"></path><path fill="url(#paint1_angular_2835_30722)" d="M7.857 11.274c-1.063 1.3-1.146 2.817-1.198 4.325-.12 3.541 1.142 6.06 1.425 6.602a11.5 11.5 0 0 0 1.53 2.248 10.7 10.7 0 0 0 2.013 1.771 6 6 0 0 1-1.742-.746 6.1 6.1 0 0 1-1.356-1.164c-.421-.551-.717-1.036-.911-1.38-.519-.921-1.165-2.068-1.495-3.605-.093-.432-.155-.87-.32-.888-.24-.025-.511.849-.57 1.037A5.1 5.1 0 0 0 5 20.944c.014 1.16.863 3.417 2.895 4.948 0 0 1.776 1.338 4.813 1.44.124.005.86-.013 1.406-.413.268-.196.445-.497-.031-.64-3.263-.977-4.536-4.514-5.233-7.583-.152-.668-.296-1.327-.383-2.01-.26-2.063-.25-4.332.405-6.322-.498.114-.724.553-1.015.91"></path></g></g><defs><radialGradient id="paint0_angular_2835_30722" cx="0" cy="0" r="1" gradientTransform="matrix(0 10.6667 -10 0 15 16.667)" gradientUnits="userSpaceOnUse"><stop stop-color="#92780F"></stop><stop offset="1" stop-color="#E8BA00"></stop></radialGradient><radialGradient id="paint1_angular_2835_30722" cx="0" cy="0" r="1" gradientTransform="matrix(0 10.6667 -10 0 15 16.667)" gradientUnits="userSpaceOnUse"><stop stop-color="#92780F"></stop><stop offset="1" stop-color="#E8BA00"></stop></radialGradient></defs></svg>
              </>}
            {location !== 'uz' && <>
                <Image src="/assets/images/payment/visa.svg" alt="Visa" width={40} height={14} />
                <Image src="/assets/images/payment/mastercard.svg" alt="Mastercard" width={40} height={14} />
              </>}
            {paymentIcon.includes('sectigo-logo') ? getThemedImage('/assets/images/ssl/sectigo-logo.svg', '/assets/images/ssl/sectigo-logo-dark.svg', 'Secure', {
            width: 100,
            height: 14
          }) : <Image src={paymentIcon} alt="Secure" width={80} height={14} />}
          </div>
        </div>
      </div>
    </footer>;
};
export default Footer;